import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CalendarCheck } from "lucide-react";
import { inputCls } from "./Contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOT_TIMES = [];
for (let h = 9; h < 17; h++) {
    SLOT_TIMES.push(`${String(h).padStart(2, "0")}:00`);
    SLOT_TIMES.push(`${String(h).padStart(2, "0")}:30`);
}

const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmt12 = (t) => {
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

export const BookingForm = () => {
    const days = useMemo(() => {
        const out = [];
        const d = new Date();
        while (out.length < 10) {
            d.setDate(d.getDate() + 1);
            if (d.getDay() > 0 && d.getDay() < 6) out.push(new Date(d));
        }
        return out;
    }, []);

    const [dayIdx, setDayIdx] = useState(0);
    const [time, setTime] = useState("");
    const [taken, setTaken] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", business: "", notes: "" });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);

    const dateStr = iso(days[dayIdx]);

    useEffect(() => {
        setTime("");
        setLoadingSlots(true);
        axios
            .get(`${API}/bookings/slots`, { params: { date: dateStr } })
            .then((r) => setTaken(r.data.taken))
            .catch(() => setTaken([]))
            .finally(() => setLoadingSlots(false));
    }, [dateStr]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!time) {
            toast.error("Pick a time slot first ♡");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/bookings`, {
                ...form,
                date: dateStr,
                time,
            });
            setDone({
                day: days[dayIdx].toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                }),
                time: fmt12(time),
                email: form.email,
            });
            setTaken((prev) => [...prev, time]);
            toast.success("You're booked — see you soon ♡");
        } catch (err) {
            if (err.response?.status === 409) {
                toast.error("That slot was just taken — pick another ♡");
                const r = await axios.get(`${API}/bookings/slots`, { params: { date: dateStr } });
                setTaken(r.data.taken);
                setTime("");
            } else {
                toast.error("Something went wrong — please email us instead.");
            }
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid="booking-success"
                className="bg-[#141414] rounded-[2rem] border border-[#CFCCC6]/50 p-10 md:p-14 text-center"
            >
                <CalendarCheck size={36} className="mx-auto text-[#F1F1EF]" />
                <p className="mt-4 font-hand text-4xl text-[#F1F1EF]">you&apos;re booked ♡</p>
                <h3 className="mt-3 font-serif-ed text-3xl tracking-tight text-[#F1F1EF]">
                    {done.day} at {done.time} CT
                </h3>
                <p className="mt-3 text-[#A9A6A0]">
                    A confirmation is on its way to {done.email}. Talk soon!
                </p>
                <button
                    data-testid="booking-again-btn"
                    onClick={() => {
                        setDone(null);
                        setTime("");
                        setForm({ name: "", email: "", business: "", notes: "" });
                    }}
                    className="mt-6 text-sm font-semibold text-[#D3A0A7] link-underline"
                >
                    Book another call
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="booking-form"
            className="bg-[#101010] rounded-[2rem] border border-[#262626] shadow-[0_25px_60px_-25px_rgba(0,0,0,0.6)] p-7 md:p-10 space-y-5"
        >
            <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#D3A0A7] mb-3">
                    1 · Pick a day
                </p>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                    {days.map((d, i) => (
                        <button
                            type="button"
                            key={iso(d)}
                            data-testid={`booking-day-${i}`}
                            onClick={() => setDayIdx(i)}
                            className={`shrink-0 w-16 py-3 rounded-2xl border text-center transition-colors duration-300 ${
                                i === dayIdx
                                    ? "bg-[#262626] border-[#262626] text-[#F1F1EF]"
                                    : "border-[#262626] text-[#F1F1EF] hover:border-[#262626]"
                            }`}
                        >
                            <span className="block text-[11px] font-semibold uppercase tracking-wide opacity-70">
                                {d.toLocaleDateString("en-US", { weekday: "short" })}
                            </span>
                            <span className="block font-serif-ed text-xl leading-tight">
                                {d.getDate()}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#D3A0A7] mb-3">
                    2 · Pick a time <span className="normal-case font-normal">(Central Time)</span>
                </p>
                {loadingSlots ? (
                    <p className="text-sm text-[#A9A6A0] py-4 inline-flex items-center gap-2">
                        <Loader2 size={15} className="animate-spin" /> Checking availability…
                    </p>
                ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {SLOT_TIMES.map((t) => {
                            const isTaken = taken.includes(t);
                            return (
                                <button
                                    type="button"
                                    key={t}
                                    data-testid={`booking-slot-${t.replace(":", "")}`}
                                    disabled={isTaken}
                                    onClick={() => setTime(t)}
                                    className={`py-2.5 rounded-xl border text-sm font-semibold transition-colors duration-300 ${
                                        isTaken
                                            ? "border-[#262626] text-[#A9A6A0]/40 line-through cursor-not-allowed"
                                            : t === time
                                              ? "bg-[#D3A0A7] border-[#D3A0A7] text-[#0C0C0C]"
                                              : "border-[#262626] text-[#F1F1EF] hover:border-[#D3A0A7] hover:text-[#D3A0A7]"
                                    }`}
                                >
                                    {fmt12(t)}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#D3A0A7] mb-3">
                    3 · Your details
                </p>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                            required
                            data-testid="booking-name-input"
                            placeholder="Your name"
                            value={form.name}
                            onChange={set("name")}
                            className={inputCls}
                        />
                        <input
                            required
                            type="email"
                            data-testid="booking-email-input"
                            placeholder="Email address"
                            value={form.email}
                            onChange={set("email")}
                            className={inputCls}
                        />
                    </div>
                    <input
                        data-testid="booking-business-input"
                        placeholder="Business name (optional)"
                        value={form.business}
                        onChange={set("business")}
                        className={inputCls}
                    />
                    <textarea
                        rows={2}
                        data-testid="booking-notes-input"
                        placeholder="Anything we should know before the call? (optional)"
                        value={form.notes}
                        onChange={set("notes")}
                        className={`${inputCls} resize-none`}
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit-btn"
                className="group w-full inline-flex items-center justify-center gap-2 bg-[#D3A0A7] text-[#0C0C0C] font-semibold text-sm px-7 py-4 rounded-full hover:bg-[#F1F1EF] transition-colors duration-300 disabled:opacity-60"
            >
                {sending ? (
                    <>
                        Booking <Loader2 size={16} className="animate-spin" />
                    </>
                ) : (
                    <>
                        Confirm My Call
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                    </>
                )}
            </button>
        </form>
    );
};
